import {isUndefined} from 'lodash';
import React, {Component, ReactNode} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../../libs/shared/helpers/src/browser/browserHelpers';
import {Handler, HandlerOf, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeaderProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {dropdownListHorizontalPadding} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {buildCancelTokenSource, CancelError, CancelTokenSource} from '../../../../helpers/asyncCancelHelpers';
import {AsyncLoader} from '../../loaders/asyncLoader';
import {DropdownEmpty} from '../dropdownEmpty';
import {DropdownLoader} from '../dropdownLoader';
import {dropdownListDefaultRowHeight, DropdownVirtualList, ScrollHandler} from '../dropdownVirtualList';
import {DropdownResults, DropdownSource} from '../source/dropdownSource';
import {
  SearchDropdownHeader,
  searchDropdownHeaderHeight,
  SearchDropdownHeaderOptionalProps,
} from './searchDropdownHeader';
import {computeIsNearBottomOfScollableList} from './searchDropdownHelpers';

/*
 * Props.
 */

interface LegacySearchDropdownProps
  extends DropdownProps,
    DropdownHeaderProps,
    SearchDropdownHeaderOptionalProps {
  /** Data source: search items based on query typed by user. */
  source: DropdownSource;
  /** How long should we keep displaying previous results to avoid flicker (default = 300). */
  gracePeriod?: number;
  /** Accessible label for the "Back" button. */
  backButtonLabel?: ReactNode;
  /** Handler for a click on the "Back" button. */
  onBackButtonClick?: Handler;
  subHeader?: {
    node: ReactNode;
    height: number;
  };
  /** Current search query, if used as a controlled component. */
  searchQuery?: string;
  /** Optional function that is called when the query changes. */
  onSearchQueryChange?: HandlerOf<string>;
  /** The initial row that is selected. */
  defaultHighlightedIndex?: number;
  /** Optional footer rendered below results. */
  footer?: React.ReactNode;
  /** Custom renderer when loading */
  renderLoading?: Renderer;
  /** Max height of the content */
  maxContentHeight?: number;
  isEditingWorkspace?: boolean;
}

/*
 * Constants.
 */

const defaultSearchDropdownWidth = 260;
const defaultGracePeriod = 300;

/*
 * Style.
 */

const StyledLoaderDiv = styled.div`
  height: 100%;
  ${center()};
`;

/*
 * Component.
 */

interface SearchDropdownState {
  searchQuery: string;
  status: AsyncStatusesEnum;
  results?: DropdownResults;
}

/*
 * This component is deprecated.
 *
 * The logic was separated into the new SearchDropdown.tsx, which is a presentational component,
 * and NetworkSearchDropdown.tsx, which adds the data fetching functionality that this component handled.
 *
 * Migration path:
 *
 * SearchDropdown: If you already have the data, and only need to filter that data on the client,
 * this is what you should use. It only handles presentation, so it can also be used if you need
 * more control over data fetching, loading / empty states, and rendering custom contet. This covers
 * the old SyncDropdownSource use case.
 *
 * NetworkSearchDropdown: If you want the component to make network requests for more data, use this.
 * You provide a fetching function, and it handles both fetching data on query change and when more data
 * is requested as the user infinite scrolls. This covers the AsyncDropdownSource use case.
 *
 * See the asyncSearchDropdown and searchDropdown stories for example usage.
 */
export class DeprecatedSearchDropdown extends Component<LegacySearchDropdownProps, SearchDropdownState> {
  constructor(props: LegacySearchDropdownProps) {
    super(props);
    this.state = {
      searchQuery: '',
      status: AsyncStatusesEnum.LOADING,
    };
  }

  private gracePeriodTimeout?: number;
  private searchCancelTokenSource?: CancelTokenSource;

  /*
   * Lifecycle.
   */

  static getDerivedStateFromProps(
    props: Readonly<LegacySearchDropdownProps>,
  ): Partial<SearchDropdownState> | null {
    // If used as a controlled component, synchronize the state.
    if (!isUndefined(props.searchQuery)) {
      return {searchQuery: props.searchQuery};
    }

    return null;
  }

  componentDidMount() {
    this.performSearchFromStateAsync();
  }

  componentDidUpdate(
    prevProps: Readonly<LegacySearchDropdownProps>,
    prevState: Readonly<SearchDropdownState>,
  ) {
    // Only if the source or the search query changed, update the results.
    if (
      prevProps.source === this.props.source &&
      prevState.searchQuery.trim() === this.state.searchQuery.trim()
    ) {
      return;
    }

    this.performSearchFromStateAsync();
  }

  componentWillUnmount() {
    clearTimeout(this.gracePeriodTimeout);
    this.maybeCancelRequest();
  }

  /*
   * Search logic.
   */

  private readonly performSearchFromStateAsync = () => {
    this.performSearchAsync(this.state.searchQuery);
  };

  private async performSearchAsync(searchQuery: string) {
    // Cancel any existing request.
    this.maybeCancelRequest();

    try {
      // Perform the search.
      this.startGracePeriod();
      this.searchCancelTokenSource = buildCancelTokenSource();
      const results = await this.props.source.searchAsync(searchQuery, this.searchCancelTokenSource.token);

      clearTimeout(this.gracePeriodTimeout);
      this.searchCancelTokenSource = undefined;

      // Update the state with the results.
      this.setState({results, status: AsyncStatusesEnum.LOADED});
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        return;
      }

      clearTimeout(this.gracePeriodTimeout);
      this.searchCancelTokenSource = undefined;

      // Otherwise, display a failed status.
      console.error(error);
      this.setState({status: AsyncStatusesEnum.FAILED});
    }
  }

  private async maybeLoadMoreAsync() {
    // If there's already a request in progress, of if we can't load more, nothing to do.
    const {results} = this.state;
    if (this.searchCancelTokenSource || !results || !results.canLoadMore || !results.loadMoreAsync) {
      return;
    }

    // Otherwise, try to load more results.
    try {
      this.searchCancelTokenSource = buildCancelTokenSource();
      const newResults = await results.loadMoreAsync(this.searchCancelTokenSource.token);

      // Update the state with the results.
      this.searchCancelTokenSource = undefined;
      this.setState({results: newResults});
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        return;
      }

      // Otherwise, display a failed status.
      console.error(error);
      this.searchCancelTokenSource = undefined;
      this.setState({status: AsyncStatusesEnum.FAILED});
    }
  }

  private startGracePeriod() {
    // If there's already one, clear it.
    clearTimeout(this.gracePeriodTimeout);

    // Start a new one.
    this.gracePeriodTimeout = globalWindow.setInstrumentedTimeout(
      () => this.setState({status: AsyncStatusesEnum.LOADING}),
      this.props.gracePeriod || defaultGracePeriod,
    );
  }

  private maybeCancelRequest() {
    return this.searchCancelTokenSource && this.searchCancelTokenSource.cancel();
  }

  /*
   * Event handlers.
   */

  private readonly onSearchChange: HandlerOf<string> = (value) => {
    const searchQuery = value;

    // Note: if used as a controlled component, any re-renders from the change handler will be batched.
    this.setState({searchQuery});

    if (this.props.onSearchQueryChange) {
      this.props.onSearchQueryChange(searchQuery);
    }
  };

  private readonly onScroll: ScrollHandler = (scrollContext) => {
    // Only load more if we're near the bottom of the list.
    const isNearBottom = computeIsNearBottomOfScollableList(scrollContext);
    if (!isNearBottom) {
      return;
    }

    this.maybeLoadMoreAsync();
  };

  /*
   * Render.
   */

  render() {
    return (
      <Dropdown {...this.props} width={this.props.width || defaultSearchDropdownWidth}>
        <SearchDropdownHeader
          {...this.props}
          value={this.state.searchQuery}
          onChange={this.onSearchChange}
          leftIcon={maybeRenderBackButton(this.props)}
        />
        {this.props.subHeader && this.props.subHeader.node}
        {this.renderContent()}
        {this.props.footer}
      </Dropdown>
    );
  }

  private renderContent() {
    const {maxContentHeight, height, width, subHeader, defaultHighlightedIndex, renderLoading} = this.props;
    const {status, results} = this.state;

    if (status !== AsyncStatusesEnum.LOADED) {
      return (
        (renderLoading && renderLoading()) || (
          <DropdownLoader status={status} onRetryClick={this.performSearchFromStateAsync} />
        )
      );
    }

    // Empty state.
    if (!results || results.rowCount === 0) {
      return (results && results.renderEmpty()) || <DropdownEmpty />;
    }

    // Add an extra item at the end for the loader.
    const rowCount = results.rowCount + (results.canLoadMore ? 1 : 0);

    const subHeaderHeight = subHeader ? subHeader.height : 0;
    const defaultMaxHeight = Math.round(dropdownListHorizontalPadding + dropdownListDefaultRowHeight * 8.5);

    return (
      <DropdownVirtualList
        key={results.query + results.refreshKey} // TODO: Tie key to the results.
        width={width || defaultSearchDropdownWidth}
        maxHeight={
          maxContentHeight ??
          (height ? height - searchDropdownHeaderHeight - subHeaderHeight : defaultMaxHeight)
        }
        rowCount={rowCount}
        selectableRowCount={results.selectableRowCount}
        renderRow={(index) => this.renderResultsRow(results, index)}
        getRowHeight={(index) => this.getRowHeight(results, index)}
        onRequestClose={this.props.onRequestClose}
        onScroll={this.onScroll}
        shouldSelectFirst={this.state.searchQuery.length > 0 ? true : undefined}
        defaultHighlightedIndex={defaultHighlightedIndex}
      />
    );
  }

  private readonly renderResultsRow = (results: DropdownResults, index: number) => {
    const {searchQuery} = this.state;

    // Render loader for infinite scroll?
    if (results.canLoadMore && index === results.rowCount) {
      return (
        <StyledLoaderDiv>
          <AsyncLoader
            status={AsyncStatusesEnum.LOADING}
            size={VisualSizesEnum.MEDIUM}
            onRetryClick={this.performSearchFromStateAsync}
          />
        </StyledLoaderDiv>
      );
    }

    // Otherwise, render a regular row.
    const isSearching = searchQuery.trim().length > 0;
    return results.renderRow(index, isSearching);
  };

  private readonly getRowHeight = (results: DropdownResults, index: number) => results.getRowHeight(index);
}

function maybeRenderBackButton(props: LegacySearchDropdownProps) {
  if (!props.backButtonLabel || !props.onBackButtonClick) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <IconButton
          iconName="chevronLeftSmall"
          label={props.backButtonLabel}
          onClick={props.onBackButtonClick}
          size={VisualSizesEnum.SMALL}
          color={props.isEditingWorkspace ? theme.greys.shade70 : undefined}
          hoverColor={props.isEditingWorkspace ? theme.greys.shade80 : undefined}
        />
      )}
    </UserThemeConsumer>
  );
}
